/**
 * Utility functions for date validation across the application
 */

export interface DateValidationResult {
  isValid: boolean;
  errorMessage?: string;
}

/**
 * Validates absence dates according to business rules
 */
export const validateAbsenceDates = (
  startDate: string,
  endDate?: string,
  displayDate?: string
): DateValidationResult => {
  const today = new Date();
  today.setHours(0, 0, 0, 0); // Reset time to start of day

  // Helper to parse YYYY-MM-DD into a local date for comparison
  const parseLocalDate = (dateStr: string) => {
    const [y, m, d] = dateStr.split('-').map(Number);
    return new Date(y, m - 1, d);
  };

  const startDateObj = parseLocalDate(startDate);

  // Validate end date is after or same as start date (only if end date is provided)
  if (endDate) {
    const endDateObj = parseLocalDate(endDate);
    if (endDateObj < startDateObj) {
      return {
        isValid: false,
        errorMessage: `End Date (${endDate}) must be on or after Start Date (${startDate}).`,
      };
    }
  }

  // Validate display date logic if provided
  if (displayDate) {
    const displayDateObj = parseLocalDate(displayDate);

    // Display date can be before start date (to show absence in advance)
    // No validation needed for display date vs start date

    // Only validate against end date if it's provided
    if (endDate) {
      const endDateObj = parseLocalDate(endDate);

      // Display date should not be after end date (absence should be visible before it ends)
      if (displayDateObj > endDateObj) {
        return {
          isValid: false,
          errorMessage: `Display Date (${displayDate}) must be on or before the End Date (${endDate}).`,
        };
      }
    }
  }

  return { isValid: true };
};

/**
 * Validates practice closure dates according to business rules
 * Note: This function validates basic date logic only.
 * The component handles new vs. existing closure logic separately.
 */
export const validateClosureDates = (
  startDate: string,
  endDate?: string,
  displayDate?: string
): DateValidationResult => {
  // Helper to parse YYYY-MM-DD into a local date for comparison
  const parseLocalDate = (dateStr: string) => {
    const [y, m, d] = dateStr.split('-').map(Number);
    return new Date(y, m - 1, d);
  };

  const startDateObj = parseLocalDate(startDate);

  // Validate end date is after or same as start date
  if (endDate) {
    const endDateObj = parseLocalDate(endDate);
    if (endDateObj < startDateObj) {
      return {
        isValid: false,
        errorMessage: 'End Date must be on or after Start Date.',
      };
    }
  }

  // Validate display date logic if provided
  if (displayDate) {
    const displayDateObj = parseLocalDate(displayDate);

    // Display date can be before start date (to show closure in advance)
    // No validation needed for display date vs start date

    // Only validate against end date if it's provided
    if (endDate) {
      const endDateObj = parseLocalDate(endDate);

      // Display date should not be after end date (closure should be visible before it ends)
      if (displayDateObj > endDateObj) {
        return {
          isValid: false,
          errorMessage: `Display Date (${displayDate}) must be before or equal to End Date (${endDate}).`,
        };
      }
    }
  }

  return { isValid: true };
};
